import React, { useEffect, useMemo, useState } from 'react';
import { Info, RefreshCw, Target } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ResolvedPeriod } from '../PeriodPicker';
import { QualityApi, RiskHeatmapCell, RiskHeatmapResponse } from './qualityApi';
import { DrillDownModal } from './DrillDownModal';

interface Props {
  resolvedPeriod: ResolvedPeriod | null;
}

/**
 * Component × Release risk heatmap. Cell colour encodes a composite `score`
 * returned by the backend; clicking a cell opens the existing component
 * drill-down filtered to that component (release filter is not yet plumbed
 * through the drill-down endpoint, so clicking still shows all releases —
 * noted as a follow-up).
 */
export const RiskHeatmap: React.FC<Props> = ({ resolvedPeriod }) => {
  const [data, setData] = useState<RiskHeatmapResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [drill, setDrill] = useState<{ component: string } | null>(null);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await QualityApi.getRiskHeatmap({ period: null, resolvedPeriod });
        if (!cancelled) setData(res);
      } catch (err) {
        if (!cancelled) {
          setData(null);
          setError(err instanceof Error ? err.message : 'Failed to load risk heatmap');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedPeriod?.startDate, resolvedPeriod?.endDate]);

  const cellsByKey = useMemo(() => {
    const map = new Map<string, RiskHeatmapCell>();
    for (const c of data?.cells ?? []) map.set(`${c.component}||${c.release}`, c);
    return map;
  }, [data]);

  const maxScore = data?.maxScore ?? 0;

  return (
    <div className="chart-card quality-risk-heatmap">
      <div className="chart-header">
        <h3 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Target size={18} />
          Risk heatmap · Component × Release
          <span
            className="stat-hint-icon"
            title="Composite risk = bug volume + open share + escaped defects + reopens. Darker = riskier. Click a cell to drill into the matching bugs."
            aria-label="About risk heatmap"
          >
            <Info size={14} />
          </span>
        </h3>
      </div>
      {loading ? (
        <p className="quality-breakdown__empty">Loading…</p>
      ) : error ? (
        <p className="quality-breakdown__empty">Failed to load: {error}</p>
      ) : !data || data.components.length === 0 || data.releases.length === 0 ? (
        <div className="quality-breakdown__empty dynamics-empty-diagnostic">
          <p>
            <strong>No components or releases found for this Period.</strong>
          </p>
          <p>
            Either widen the Period, or make sure bugs actually have <code>components</code> and
            <code> fix_version</code> set in Jira.
          </p>
          <p style={{ marginTop: 8 }}>
            <Link
              to="/analytics?tab=database"
              className="dynamics-empty-diagnostic__link"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              <RefreshCw size={12} />
              Run a Full Update
            </Link>
          </p>
        </div>
      ) : (
        <div className="quality-risk-heatmap__scroll">
          <table className="quality-risk-heatmap__grid">
            <thead>
              <tr>
                <th aria-label="Component" />
                {data.releases.map((r) => (
                  <th key={r} title={r}>
                    <span>{r}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.components.map((component) => (
                <tr key={component}>
                  <th scope="row" title={component}>
                    {component}
                  </th>
                  {data.releases.map((release) => {
                    const cell = cellsByKey.get(`${component}||${release}`);
                    const score = cell?.score ?? 0;
                    const intensity = maxScore > 0 ? Math.min(1, score / maxScore) : 0;
                    return (
                      <td
                        key={`${component}||${release}`}
                        className="quality-risk-heatmap__cell"
                        style={{
                          backgroundColor: intensity
                            ? `rgba(239, 68, 68, ${0.12 + intensity * 0.72})`
                            : undefined,
                          color: intensity > 0.6 ? '#fff' : undefined,
                        }}
                        title={
                          cell
                            ? `${component} × ${release}\nBugs: ${cell.bugs}\nOpen: ${cell.open}\nEscaped: ${cell.escaped}\nReopens: ${cell.reopens}\nScore: ${cell.score}`
                            : 'No data'
                        }
                        onClick={() => cell && setDrill({ component })}
                      >
                        {cell && cell.bugs > 0 ? cell.bugs : ''}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {drill && (
        <DrillDownModal
          by="component"
          value={drill.component}
          resolvedPeriod={resolvedPeriod}
          onClose={() => setDrill(null)}
        />
      )}
    </div>
  );
};
